import type { Permission } from '@contract-analyzer/api-client';
import { UploadBatchProvider } from '@/components/providers/upload-batch-provider';
import { lazy, Suspense, type ComponentType, type LazyExoticComponent, type ReactElement } from 'react';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  type RoutesProps,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';

import { FullPageState, PageLoadingState } from '@/components/common';
import { AuthGuard } from '@/components/layout/auth-guard';
import { AuthLoadingScreen } from '@/components/layout/auth-loading';
import { loadPageChunk, renderLazyPage } from '@/components/layout/lazy-page';
import { PermissionGuard } from '@/components/layout/permission-guard';
import { OnboardingGuard } from '@/components/layout/onboarding-guard';
import { TermsGuard } from '@/components/layout/terms-guard';
import { AppLayout, FolioLayoutRoute } from '@/components/layout/app-layout';
import { AuthProvider } from '@/components/providers/auth-provider';
import { ProjectProvider } from '@/components/providers/project-provider';
import { TenantProvider } from '@/components/providers/tenant-provider';
import { Button } from '@/components/ui/button';
import { Toaster } from '@/components/ui/sonner';
import { useAuth } from '@/hooks/use-auth';
import { useProjectPermissions } from '@/hooks/use-project-permissions';
import { controlPlaneDemoEnabled } from '@/lib/control-plane';
import { readRedirectPath, readRedirectPathFromSearchParams } from '@/lib/redirect-path';
import { projectExecutionLogPath, projectPath, tenantPath } from '@/lib/routes';

/**
 * Redirects superadmins without an active workspace (tenant_id === null) to /admin.
 * Non-superadmins always pass through.
 */
function WorkspaceGuard() {
  const { user } = useAuth();

  if (user?.is_superadmin === true && user.tenant_id === null) {
    return <Navigate to="/admin" replace />;
  }

  return <Outlet />;
}

/** Show landing page for first-time visitors; login for returning users. */
function EntryRoute() {
  const {
    error,
    isAuthenticated,
    isLoading,
    onboardingRequest,
    requiresOnboarding,
    user,
  } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (isAuthenticated) {
    if (user?.is_superadmin !== true && (user === null || user.membership_id === null)) {
      return <Navigate to="/invitations" replace />;
    }

    const fallbackTarget = user.tenant_slug ? `/${user.tenant_slug}` : '/admin';
    const target = readRedirectPath(
      location.state,
      readRedirectPathFromSearchParams(searchParams, fallbackTarget),
    );
    return <Navigate to={target} replace />;
  }

  if (requiresOnboarding) {
    return <Navigate to={onboardingRequest === null ? '/onboarding' : '/onboarding/pending'} replace />;
  }

  // Explicit ?signin param from landing CTAs bypasses the first-visit check
  const explicitSignin = searchParams.has('signin');
  const hasVisited = localStorage.getItem('contract-analyzer:has-visited') === '1';
  const hasProtectedDestination = readRedirectPath(location.state, '') !== '';

  if (error !== null) {
    return renderLazyPage(LoginPage);
  }

  return hasVisited || explicitSignin || hasProtectedDestination
    ? renderLazyPage(LoginPage)
    : renderLazyPage(LandingPage);
}

function TenantRedirect() {
  const { user } = useAuth();
  const target = user?.tenant_slug ? `/${user.tenant_slug}` : '/admin';
  return <Navigate to={target} replace />;
}

function TenantCatalogRedirect() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return (
    <Navigate
      replace
      to={tenantSlug === undefined ? '/' : tenantPath(tenantSlug, '/projects')}
    />
  );
}

function ProjectUploadRedirect() {
  const { projectId, tenantSlug } = useParams<{ projectId: string; tenantSlug: string }>();

  if (!projectId || !tenantSlug) {
    return <Navigate to=".." replace />;
  }

  return <Navigate to={projectPath(tenantSlug, projectId, '/upload-documents')} replace />;
}

function ProjectExecutionLogRedirect() {
  const { projectId, runId, tenantSlug } = useParams<{
    projectId: string;
    runId: string;
    tenantSlug: string;
  }>();
  if (projectId === undefined || tenantSlug === undefined) {
    return <Navigate to=".." replace />;
  }
  return (
    <Navigate
      replace
      to={projectExecutionLogPath(
        tenantSlug,
        projectId,
        runId === undefined ? undefined : { runId },
      )}
    />
  );
}

type ProjectRoutePermissionMode = 'all' | 'any';

interface ProjectRoutePermissionGuardProps {
  readonly permissions: readonly Permission[];
  readonly mode?: ProjectRoutePermissionMode;
  readonly loadingLabel?: string;
  readonly deniedMessage: string;
}

function ProjectRoutePermissionGuard({
  permissions,
  mode = 'all',
  loadingLabel = 'Checking project access…',
  deniedMessage,
}: ProjectRoutePermissionGuardProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    error: permissionsError,
    hasPermission,
    isLoading: permissionsLoading,
    permissions: projectPermissions,
    reload: reloadPermissions,
  } = useProjectPermissions(projectId ?? null);

  if (projectId === undefined) {
    return (
      <FullPageState
        title="Project access required"
        message="Choose a project before opening this page."
      />
    );
  }

  if (permissionsLoading && projectPermissions.length === 0) {
    return <PageLoadingState variant="compact" label={loadingLabel} />;
  }

  if (permissionsError !== null) {
    return (
      <FullPageState
        title="Failed to verify project access"
        message={permissionsError}
        tone="error"
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              void reloadPermissions();
            }}
          >
            Retry
          </Button>
        }
      />
    );
  }

  const isAllowed =
    mode === 'any'
      ? permissions.some((permission) => hasPermission(permission))
      : permissions.every((permission) => hasPermission(permission));

  if (!isAllowed) {
    return <FullPageState title="Access denied" message={deniedMessage} tone="error" />;
  }

  return <Outlet />;
}

const LazyAgentation = lazy(() =>
  import('agentation').then((m) => ({ default: m.Agentation })),
);

const APP_BASE_PATH = import.meta.env.BASE_URL.replace(/\/+$/, '');

function getAgentationEndpoint(): string {
  const configuredEndpoint = import.meta.env.VITE_AGENTATION_ENDPOINT?.trim();
  if (configuredEndpoint && configuredEndpoint.length > 0) {
    return configuredEndpoint;
  }

  if (
    typeof window === 'undefined' ||
    !window.location.protocol ||
    !window.location.hostname ||
    window.location.origin === 'null'
  ) {
    return 'https://localhost:4747';
  }

  return `${window.location.protocol}//${window.location.hostname}:4747`;
}

function lazyPage<ExportName extends string>(
  load: () => Promise<Record<ExportName, ComponentType<object>>>,
  exportName: ExportName,
): LazyExoticComponent<ComponentType<object>> {
  return lazy(async () => {
    const module = await loadPageChunk(load);
    return { default: module[exportName] };
  });
}

const AdminPage = lazyPage(() => import('@/pages/admin'), 'AdminPage');
const AdminWorkspaceCreatePage = lazyPage(
  () => import('@/pages/admin-workspace-create'),
  'AdminWorkspaceCreatePage',
);
const AdminWorkspaceDetailPage = lazyPage(
  () => import('@/pages/admin-workspace-detail'),
  'AdminWorkspaceDetailPage',
);
const AnalysisResultRedirectPage = lazyPage(
  () => import('@/pages/analysis-result-redirect'),
  'AnalysisResultRedirectPage',
);
const CliLoginApprovalPage = lazyPage(
  () => import('@/pages/cli-login-approval'),
  'CliLoginApprovalPage',
);
const ControlPlanePage = lazyPage(
  () => import('@/pages/control-plane'),
  'ControlPlanePage',
);
const DashboardPage = lazyPage(() => import('@/pages/dashboard'), 'DashboardPage');
const DocumentDetailPage = lazyPage(
  () => import('@/pages/document-detail'),
  'DocumentDetailPage',
);
const DocumentsPage = lazyPage(() => import('@/pages/documents'), 'DocumentsPage');
const EvalComparisonPage = lazyPage(() => import('@/pages/eval-comparison'), 'EvalComparisonPage');
const EvalRunDetailPage = lazyPage(() => import('@/pages/eval-run-detail'), 'EvalRunDetailPage');
const EvaluationsAndGroundTruthPage = lazyPage(
  () => import('@/pages/evaluations-and-ground-truth'),
  'EvaluationsAndGroundTruthPage',
);
const GroundTruthAnnotatePage = lazyPage(
  () => import('@/pages/ground-truth-annotate'),
  'GroundTruthAnnotatePage',
);
const LandingPage = lazyPage(() => import('@/pages/landing'), 'LandingPage');
const LegacyDemoRedirectPage = lazyPage(
  () => import('@/pages/legacy-demo-redirect'),
  'LegacyDemoRedirectPage',
);
const LoginPage = lazyPage(() => import('@/pages/login'), 'LoginPage');
const NotFoundPage = lazyPage(() => import('@/pages/not-found'), 'NotFoundPage');
const OnboardingChoicePage = lazyPage(
  () => import('@/pages/onboarding-choice'),
  'OnboardingChoicePage',
);
const PendingApprovalPage = lazyPage(
  () => import('@/pages/pending-approval'),
  'PendingApprovalPage',
);
const PendingInvitationsPage = lazyPage(
  () => import('@/pages/pending-invitations'),
  'PendingInvitationsPage',
);
const ProjectDetailPage = lazyPage(() => import('@/pages/project-detail'), 'ProjectDetailPage');
const ProjectMembersPage = lazyPage(
  () => import('@/pages/project-members'),
  'ProjectMembersPage',
);
const ProjectSettingsPage = lazyPage(
  () => import('@/pages/project-settings'),
  'ProjectSettingsPage',
);
const ProjectTemplatesPage = lazyPage(
  () => import('@/pages/project-templates'),
  'ProjectTemplatesPage',
);
const ProjectsPage = lazyPage(() => import('@/pages/projects'), 'ProjectsPage');
const RequestJoinPage = lazyPage(() => import('@/pages/request-join'), 'RequestJoinPage');
const RequestNewTenantPage = lazyPage(
  () => import('@/pages/request-new-tenant'),
  'RequestNewTenantPage',
);
const SettingsPage = lazyPage(() => import('@/pages/settings'), 'SettingsPage');
const TemplateDetailPage = lazyPage(() => import('@/pages/template-detail'), 'TemplateDetailPage');
const TemplateEditorPage = lazyPage(() => import('@/pages/template-editor'), 'TemplateEditorPage');
const TemplatesPage = lazyPage(() => import('@/pages/templates'), 'TemplatesPage');
const TSAWorkspacePage = lazyPage(() => import('@/pages/tsa-workspace'), 'TSAWorkspacePage');
const TermsAcceptancePage = lazyPage(
  () => import('@/pages/terms-acceptance'),
  'TermsAcceptancePage',
);
const UploadDocumentsPage = lazyPage(
  () => import('@/pages/upload-documents'),
  'UploadDocumentsPage',
);
const UserManagementPage = lazyPage(
  () => import('@/pages/user-management'),
  'UserManagementPage',
);
const ProjectAccessPage = lazyPage(
  () => import('@/pages/project-access'),
  'ProjectAccessPage',
);

function ProjectTabRedirect({
  tab,
  target,
}: {
  readonly tab: string;
  readonly target: 'evaluations' | 'runs';
}) {
  const params = useParams<{ projectId: string; tenantSlug: string }>();

  if (params.tenantSlug === undefined || params.projectId === undefined) {
    return <Navigate replace to="/" />;
  }

  return (
    <Navigate
      replace
      to={`/${params.tenantSlug}/projects/${params.projectId}/${target}?tab=${tab}`}
    />
  );
}

function ProjectSettingsLogsRedirect() {
  const params = useParams<{ projectId: string; tenantSlug: string }>();

  if (params.tenantSlug === undefined || params.projectId === undefined) {
    return <Navigate replace to="/" />;
  }

  return <Navigate replace to={projectExecutionLogPath(params.tenantSlug, params.projectId)} />;
}

/**
 * The route table, as elements. A constant rather than part of `App`, so the
 * route-coverage test reads the real table without rendering the app.
 */
export const APP_ROUTES: ReactElement<RoutesProps> = (
  <Routes>
    <Route element={renderLazyPage(LegacyDemoRedirectPage)} path="/demo/*" />
    <Route element={renderLazyPage(CliLoginApprovalPage)} path="/cli-login" />
    <Route element={<EntryRoute />} path="/login" />
    <Route element={renderLazyPage(LandingPage)} path="/landing" />
    {controlPlaneDemoEnabled ? (
      <Route element={<FolioLayoutRoute />}>
        <Route element={<AppLayout />}>
          <Route element={renderLazyPage(ControlPlanePage)} path="/control-plane" />
        </Route>
      </Route>
    ) : null}
    <Route element={<OnboardingGuard />}>
      <Route element={renderLazyPage(OnboardingChoicePage)} path="/onboarding" />
      <Route element={renderLazyPage(RequestNewTenantPage)} path="/onboarding/new" />
      <Route element={renderLazyPage(RequestJoinPage)} path="/onboarding/join" />
      <Route element={renderLazyPage(PendingApprovalPage)} path="/onboarding/pending" />
    </Route>
    <Route element={<AuthGuard />}>
      <Route element={renderLazyPage(PendingInvitationsPage)} path="/invitations" />
      <Route element={renderLazyPage(TermsAcceptancePage)} path="/terms" />
      <Route element={<TermsGuard />}>
        <Route element={<TenantRedirect />} path="/" />

        {/* Retired flat routes should fail fast instead of being treated as tenant slugs. */}
        <Route element={renderLazyPage(NotFoundPage)} path="/projects/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/batches/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/runs/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/documents/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/analyses/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/evaluations/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/ground-truth/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/templates/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/settings/*" />
        <Route element={renderLazyPage(NotFoundPage)} path="/admin/*" />

        {/* Global authenticated routes stay outside the tenant scope. */}
        <Route element={<FolioLayoutRoute />}>
          <Route element={<AppLayout />}>
            <Route element={renderLazyPage(SettingsPage)} path="/settings" />
            <Route element={<PermissionGuard requireSuperadmin redirectTo="/" />}>
              <Route element={renderLazyPage(AdminPage)} path="/admin" />
              {!controlPlaneDemoEnabled ? (
                <Route element={renderLazyPage(ControlPlanePage)} path="/control-plane" />
              ) : null}
              <Route
                element={renderLazyPage(AdminWorkspaceCreatePage)}
                path="/admin/workspaces/new"
              />
              <Route
                element={renderLazyPage(AdminWorkspaceDetailPage)}
                path="/admin/workspaces/:tenantId"
              />
            </Route>
          </Route>
        </Route>

        {/* Tenant and project-scoped routes live under the active tenant slug. */}
        <Route element={<TenantProvider />} path="/:tenantSlug">
          <Route element={<FolioLayoutRoute />}>
            {/* Tenant-level routes: AppLayout renders with tenant context only. */}
            <Route element={<AppLayout />}>
              <Route element={<WorkspaceGuard />}>
                <Route index element={renderLazyPage(DashboardPage)} />
                <Route element={renderLazyPage(ProjectsPage)} path="projects" />
                <Route element={<TenantCatalogRedirect />} path="runs" />
                <Route element={renderLazyPage(DocumentsPage)} path="documents" />
                <Route element={<TenantCatalogRedirect />} path="analyses" />
                <Route element={renderLazyPage(TemplatesPage)} path="templates" />
                <Route element={renderLazyPage(TemplateEditorPage)} path="templates/new" />
                <Route element={renderLazyPage(TemplateDetailPage)} path="templates/:templateId" />
                <Route
                  element={renderLazyPage(TemplateEditorPage)}
                  path="templates/:templateId/edit"
                />
                <Route element={<PermissionGuard permission="users.manage" redirectTo="/" />}>
                  <Route element={renderLazyPage(UserManagementPage)} path="members" />
                  <Route element={renderLazyPage(ProjectAccessPage)} path="project-access" />
                </Route>
                <Route element={renderLazyPage(SettingsPage)} path="settings" />
              </Route>
              <Route element={renderLazyPage(NotFoundPage)} path="*" />
            </Route>

            {/* Project-level routes: ProjectProvider wraps AppLayout so the
                shell can render project-aware navigation and breadcrumbs. */}
            <Route path="projects/:projectId">
              <Route element={<ProjectProvider />}>
                <Route element={<AppLayout />}>
                  <Route element={<WorkspaceGuard />}>
                    <Route index element={renderLazyPage(ProjectDetailPage)} />
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['documents.view']}
                          deniedMessage="You need document view access to open this project document library."
                        />
                      }
                    >
                      <Route element={renderLazyPage(DocumentsPage)} path="documents" />
                      <Route
                        element={renderLazyPage(DocumentDetailPage)}
                        path="documents/:documentId"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['runs.view', 'analyses.view']}
                          mode="any"
                          deniedMessage="You need run or analysis view access to open this project activity page."
                        />
                      }
                    >
                      <Route element={<ProjectExecutionLogRedirect />} path="runs" />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['documents.add']}
                          deniedMessage="You need document upload access to upload project documents."
                        />
                      }
                    >
                      <Route element={<ProjectUploadRedirect />} path="runs/new" />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['runs.view']}
                          deniedMessage="You need run view access to open project runs."
                        />
                      }
                    >
                      <Route element={<ProjectExecutionLogRedirect />} path="runs/:runId" />
                      <Route
                        element={<ProjectExecutionLogRedirect />}
                        path="runs/:runId/results"
                      />
                      <Route
                        element={<ProjectExecutionLogRedirect />}
                        path="documents/:runId/results"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['analyses.view']}
                          deniedMessage="You need analysis view access to open project analyses."
                        />
                      }
                    >
                      <Route
                        element={<ProjectSettingsLogsRedirect />}
                        path="analyses"
                      />
                      <Route
                        element={renderLazyPage(AnalysisResultRedirectPage)}
                        path="analyses/:analysisId"
                      />
                      <Route
                        element={renderLazyPage(AnalysisResultRedirectPage)}
                        path="analyses/:analysisId/workspace"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['runs.view', 'documents.view', 'templates.view']}
                          deniedMessage="You need run, document, and template view access to open ground truth samples."
                        />
                      }
                    >
                      <Route
                        element={<ProjectTabRedirect tab="ground-truth" target="evaluations" />}
                        path="ground-truth"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['runs.start', 'runs.view', 'documents.view', 'templates.view']}
                          deniedMessage="You need ground truth annotation access plus run, document, and template view access to annotate samples."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(GroundTruthAnnotatePage)}
                        path="ground-truth/annotate/:documentId"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['templates.view']}
                          deniedMessage="You need template view access to open project templates."
                        />
                      }
                    >
                      <Route element={renderLazyPage(ProjectTemplatesPage)} path="templates" />
                      <Route
                        element={renderLazyPage(TemplateDetailPage)}
                        path="templates/:templateId"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['templates.create']}
                          deniedMessage="You need template create access in this project to author a template."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(TemplateEditorPage)}
                        path="templates/new"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['templates.edit']}
                          deniedMessage="You need template edit access in this project to edit a template."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(TemplateEditorPage)}
                        path="templates/:templateId/edit"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['projects.view']}
                          deniedMessage="You need access to this project to view its members."
                        />
                      }
                    >
                      <Route element={renderLazyPage(ProjectMembersPage)} path="members" />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['projects.edit', 'runs.view', 'analyses.view']}
                          mode="any"
                          deniedMessage="You need project edit, run view, or analysis view access to open project settings."
                        />
                      }
                    >
                      <Route element={renderLazyPage(ProjectSettingsPage)} path="settings" />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['documents.add']}
                          deniedMessage="You need document upload access to upload documents."
                        />
                      }
                    >
                      <Route element={<ProjectUploadRedirect />} path="upload" />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['documents.add']}
                          deniedMessage="You need document upload access to upload documents."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(UploadDocumentsPage)}
                        path="upload-documents"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['runs.view']}
                          deniedMessage="You need run view access to open project Verify surfaces."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(EvaluationsAndGroundTruthPage)}
                        path="evaluations"
                      />
                      <Route
                        element={renderLazyPage(EvalComparisonPage)}
                        path="evaluations/compare"
                      />
                      <Route
                        element={renderLazyPage(EvalRunDetailPage)}
                        path="evaluations/:evalRunId"
                      />
                    </Route>
                    <Route element={renderLazyPage(TSAWorkspacePage)} path="tsa-workspace" />
                    {/* Templates nested inside the TSA workspace reuse the
                        global editor and detail components, scoped to this
                        project. Same permission gates as flat templates. */}
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['templates.create']}
                          deniedMessage="You need template create access in this project to author a TSA template."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(TemplateEditorPage)}
                        path="tsa-workspace/templates/new"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['templates.view']}
                          deniedMessage="You need template view access to open this TSA template."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(TemplateDetailPage)}
                        path="tsa-workspace/templates/:templateId"
                      />
                    </Route>
                    <Route
                      element={
                        <ProjectRoutePermissionGuard
                          permissions={['templates.edit']}
                          deniedMessage="You need template edit access in this project to edit a TSA template."
                        />
                      }
                    >
                      <Route
                        element={renderLazyPage(TemplateEditorPage)}
                        path="tsa-workspace/templates/:templateId/edit"
                      />
                    </Route>
                  </Route>
                  <Route element={renderLazyPage(NotFoundPage)} path="*" />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>

        <Route element={renderLazyPage(NotFoundPage)} path="*" />
      </Route>
    </Route>
  </Routes>
);

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter {...(APP_BASE_PATH ? { basename: APP_BASE_PATH } : {})}>
        <UploadBatchProvider>{APP_ROUTES}</UploadBatchProvider>
      </BrowserRouter>
      <Toaster position="bottom-left" />
      {import.meta.env.DEV ? (
        <Suspense>
          <LazyAgentation endpoint={getAgentationEndpoint()} />
        </Suspense>
      ) : null}
    </AuthProvider>
  );
}
